import { useRef, useState } from 'react';

import { useOverflowing } from '@/components/home/useOverflowing';
import { Container } from '@/components/ui/Container';
import { HIGHLIGHTS_SECTION, HOME_HIGHLIGHTS } from '@/data/homeContent';
import { cn } from '@/lib/cn';

/**
 * The four columns of prose on the dark green panel, under the hero.
 *
 * Two shapes. From `sm` it is the grid the design shows, and one column leads
 * at a time: its rule turns brand green and its copy comes up to full white
 * while the others sit back. The lead follows the pointer and starts on the
 * first column, so the panel has that emphasis before anyone touches it.
 *
 * On a phone the same four columns stacked ran most of a screen tall - these
 * are paragraphs, not captions - so they become a swipe rail instead, one
 * highlight at a time with the next peeking in. Nothing is dimmed there: with
 * one card in view, dimming three you cannot see is just three unreadable
 * cards waiting for you. There is no hover on a touch screen to bring them
 * back, either, so the lead treatment starts at `sm` along with the grid.
 *
 * Dimmed is never hidden in any case. Nothing here sits behind an interaction,
 * so the resting state stays at `white/60`, about 6:1 on this panel and over
 * the AA floor, rather than the near-invisible grey a visual mock can afford.
 */
export function HighlightsBand() {
  const [lead, setLead] = useState(0);
  const rail = useRef<HTMLUListElement>(null);
  const scrollable = useOverflowing(rail);

  return (
    // No vertical padding of its own: the hero's `pb` above and the features
    // section's `pt` below already set the gaps, and the dark panel inside
    // carries its own room.
    <section aria-label={HIGHLIGHTS_SECTION.label}>
      <Container>
        {/* The panel stops where every other section's content stops rather
            than running to the window's edge. Full bleed reads as a stripe on
            a 1920px monitor - a band of colour wider than anything in it. */}
        <div className="rounded-2xl bg-brand-cta px-4 py-4 sm:px-6 sm:py-5">
          <ul
            ref={rail}
            tabIndex={scrollable ? 0 : undefined}
            aria-label={HIGHLIGHTS_SECTION.label}
            className={cn(
              'no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1',
              'sm:grid sm:grid-cols-2 sm:gap-y-5 sm:overflow-visible sm:pb-0',
              'lg:grid-cols-4',
            )}
            onMouseLeave={() => setLead(0)}
          >
            {HOME_HIGHLIGHTS.map((highlight, index) => {
              const leading = index === lead;

              return (
                <li
                  key={highlight.title}
                  onMouseEnter={() => setLead(index)}
                  className="relative w-[88%] shrink-0 snap-start ps-3 sm:w-auto sm:ps-4 sm:pe-5"
                >
                  <span
                    className={cn(
                      'absolute inset-y-0 start-0 w-0.5 rounded-full transition-colors duration-300',
                      // Green on a phone whichever card this is - the rail
                      // shows one at a time, so there is no "other" to be.
                      leading ? 'bg-brand-400' : 'bg-brand-400 sm:bg-white/15',
                    )}
                    aria-hidden
                  />
                  <h2
                    className={cn(
                      'text-balance text-sm font-bold leading-snug tracking-tight transition-colors duration-300 sm:text-base',
                      leading ? 'text-brand-400' : 'text-brand-400 sm:text-white/90',
                    )}
                  >
                    {highlight.title}
                  </h2>
                  <p
                    className={cn(
                      'mt-1.5 text-[13px] leading-snug transition-colors duration-300 sm:text-sm',
                      leading ? 'text-white' : 'text-white sm:text-white/60',
                    )}
                  >
                    {highlight.description}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </Container>
    </section>
  );
}
